/**
 * Image Compression Utility
 * 
 * Compresses screenshots client-side before upload to reduce file size
 * and improve upload performance.
 * 
 * Features:
 * - JPEG compression with configurable quality
 * - Adaptive resolution (maintains aspect ratio)
 * - Performance metrics tracking
 * - Error handling with graceful fallbacks
 */

// Compression profiles
export const COMPRESSION_PROFILES = {
  // Ultra quality - maximum detail
  ultra: {
    id: 'ultra',
    name: 'Ultra Quality',
    format: 'image/jpeg',
    quality: 0.95,
    maxWidth: 2560,
    maxHeight: 1440,
    label: 'Ultra Quality',
    description: 'Best quality, larger files',
    estimatedSize: '500-800 KB',
    estimatedTime: '~500ms',
    useCase: 'Competitive analysis, detailed review',
    icon: '🏆',
  },
  
  // High quality - for detailed analysis
  high: {
    id: 'high',
    name: 'High Quality',
    format: 'image/jpeg',
    quality: 0.92,
    maxWidth: 1920,
    maxHeight: 1080,
    label: 'High Quality',
    description: 'Excellent quality, good balance',
    estimatedSize: '350-500 KB',
    estimatedTime: '~400ms',
    useCase: 'Professional gameplay review',
    icon: '⭐',
  },
  
  // Balanced - recommended default
  balanced: {
    id: 'balanced',
    name: 'Balanced',
    format: 'image/jpeg',
    quality: 0.85,
    maxWidth: 1920,
    maxHeight: 1080,
    label: 'Balanced (Recommended)',
    description: 'Optimal balance of quality and speed',
    estimatedSize: '200-400 KB',
    estimatedTime: '~300ms',
    useCase: 'General gameplay screenshots',
    isDefault: true,
    icon: '✓',
  },
  
  // Fast - for rapid capture sessions
  fast: {
    id: 'fast',
    name: 'Fast Upload',
    format: 'image/jpeg',
    quality: 0.75,
    maxWidth: 1600,
    maxHeight: 900,
    label: 'Fast Upload',
    description: 'Quick uploads, smaller files',
    estimatedSize: '100-250 KB',
    estimatedTime: '~200ms',
    useCase: 'Rapid capture, poor connections',
    icon: '⚡',
  },
  
  // Mobile - for slow connections
  mobile: {
    id: 'mobile',
    name: 'Mobile/3G',
    format: 'image/jpeg',
    quality: 0.70,
    maxWidth: 1280,
    maxHeight: 720,
    label: 'Mobile/3G',
    description: 'Optimized for slow connections',
    estimatedSize: '50-150 KB',
    estimatedTime: '~150ms',
    useCase: 'Mobile hotspot, 3G connections',
    icon: '📱',
  },
  
  // Custom - user-defined
  custom: {
    id: 'custom',
    name: 'Custom',
    format: 'image/jpeg',
    quality: 0.85, // Default, user can change
    maxWidth: 1920, // Default, user can change
    maxHeight: 1080, // Default, user can change
    label: 'Custom',
    description: 'User-defined settings',
    estimatedSize: 'Varies',
    estimatedTime: 'Varies',
    useCase: 'Advanced users',
    icon: '⚙️',
  },
};

// Default configuration
const DEFAULT_CONFIG = {
  format: 'image/jpeg',
  quality: 0.85,
  maxWidth: 1920,
  maxHeight: 1080,
};

/**
 * Calculate target dimensions maintaining aspect ratio
 * 
 * @param {number} sourceWidth - Original image width
 * @param {number} sourceHeight - Original image height
 * @param {number} maxWidth - Maximum allowed width
 * @param {number} maxHeight - Maximum allowed height
 * @returns {{width: number, height: number}} Target dimensions
 */
function calculateTargetDimensions(sourceWidth, sourceHeight, maxWidth, maxHeight) {
  let targetWidth = sourceWidth;
  let targetHeight = sourceHeight;
  
  // Only resize if exceeds max dimensions
  if (targetWidth > maxWidth || targetHeight > maxHeight) {
    const ratio = Math.min(maxWidth / targetWidth, maxHeight / targetHeight);
    targetWidth = Math.floor(targetWidth * ratio);
    targetHeight = Math.floor(targetHeight * ratio);
  }
  
  return { width: targetWidth, height: targetHeight };
}

/**
 * Compress an image blob/buffer
 * 
 * @param {Blob|ArrayBuffer} source - Source image data (PNG from Electron)
 * @param {Object} config - Compression configuration
 * @param {string} config.format - Output format (default: 'image/jpeg')
 * @param {number} config.quality - JPEG quality 0.0-1.0 (default: 0.85)
 * @param {number} config.maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} config.maxHeight - Maximum height in pixels (default: 1080)
 * @returns {Promise<Blob>} Compressed image blob
 */
export async function compressImage(source, config = {}) {
  const startTime = performance.now();
  const options = { ...DEFAULT_CONFIG, ...config };
  
  // Convert source to blob if needed
  const sourceBlob = source instanceof Blob 
    ? source 
    : new Blob([source], { type: 'image/png' });
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        console.log('[imageCompression] Image loaded:', {
          originalWidth: img.width,
          originalHeight: img.height,
          originalSize: sourceBlob.size,
        });
        
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate target dimensions
        const { width, height } = calculateTargetDimensions(
          img.width,
          img.height,
          options.maxWidth,
          options.maxHeight
        );
        
        console.log('[imageCompression] Target dimensions:', { width, height });
        
        // Set canvas size
        canvas.width = width;
        canvas.height = height;
        
        // Draw image (this performs the resize)
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressionTime = performance.now() - startTime;
              
              console.log('[imageCompression] Compression complete:', {
                format: options.format,
                quality: options.quality,
                compressedSize: blob.size,
                compressionTime: `${compressionTime.toFixed(1)}ms`,
                reduction: `${(((sourceBlob.size - blob.size) / sourceBlob.size) * 100).toFixed(1)}%`,
              });
              
              resolve(blob);
            } else {
              reject(new Error('Failed to create compressed blob'));
            }
          },
          options.format,
          options.quality
        );
        
        // Cleanup
        URL.revokeObjectURL(img.src);
      } catch (error) {
        console.error('[imageCompression] Compression error:', error);
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('[imageCompression] Image load error:', error);
      reject(new Error('Failed to load image for compression'));
    };
    
    // Load image
    img.src = URL.createObjectURL(sourceBlob);
  });
}

/**
 * Get compression statistics for monitoring
 * 
 * @param {number} originalSize - Original file size in bytes
 * @param {number} compressedSize - Compressed file size in bytes
 * @returns {Object} Compression statistics
 */
export function getCompressionStats(originalSize, compressedSize) {
  const reduction = originalSize - compressedSize;
  const reductionPercent = (reduction / originalSize) * 100;
  const compressionRatio = originalSize / compressedSize;
  
  return {
    originalSize,
    compressedSize,
    reduction,
    reductionPercent: reductionPercent.toFixed(1),
    compressionRatio: compressionRatio.toFixed(2),
    originalSizeMB: (originalSize / (1024 * 1024)).toFixed(2),
    compressedSizeMB: (compressedSize / (1024 * 1024)).toFixed(2),
    compressedSizeKB: (compressedSize / 1024).toFixed(0),
  };
}

/**
 * Format file size for display
 * 
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.4 MB", "256 KB")
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}

/**
 * Check if browser supports required compression features
 * 
 * @returns {boolean} True if compression is supported
 */
export function isCompressionSupported() {
  try {
    // Check for Canvas API
    const canvas = document.createElement('canvas');
    if (!canvas.getContext || !canvas.getContext('2d')) {
      return false;
    }
    
    // Check for toBlob support
    if (!HTMLCanvasElement.prototype.toBlob) {
      console.warn('[imageCompression] Canvas toBlob not supported');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('[imageCompression] Feature detection error:', error);
    return false;
  }
}

/**
 * Get compression profile by ID
 * 
 * @param {string} profileId - Profile ID ('ultra', 'high', 'balanced', 'fast', 'mobile', 'custom')
 * @param {Object} customSettings - Custom settings to override (for custom profile)
 * @returns {Object} Compression profile configuration
 */
export function getCompressionProfile(profileId = 'balanced', customSettings = {}) {
  const profile = COMPRESSION_PROFILES[profileId] || COMPRESSION_PROFILES.balanced;
  
  // If custom profile, merge with custom settings
  if (profileId === 'custom' && Object.keys(customSettings).length > 0) {
    return {
      ...profile,
      quality: customSettings.quality ?? profile.quality,
      maxWidth: customSettings.resolution?.width ?? profile.maxWidth,
      maxHeight: customSettings.resolution?.height ?? profile.maxHeight,
    };
  }
  
  return profile;
}

/**
 * Get all profile IDs
 * 
 * @returns {Array<string>} Array of profile IDs
 */
export function getAllProfileIds() {
  return Object.keys(COMPRESSION_PROFILES);
}

/**
 * Get all profiles (excluding custom)
 * 
 * @returns {Array<Object>} Array of profile objects
 */
export function getAllProfiles() {
  return Object.values(COMPRESSION_PROFILES).filter(p => p.id !== 'custom');
}

/**
 * Validate custom resolution
 * 
 * @param {number} width - Width in pixels
 * @param {number} height - Height in pixels
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validateResolution(width, height) {
  const MIN_WIDTH = 640;
  const MIN_HEIGHT = 360;
  const MAX_WIDTH = 3840;
  const MAX_HEIGHT = 2160;
  
  if (!width || !height || isNaN(width) || isNaN(height)) {
    return { valid: false, error: 'Width and height must be valid numbers' };
  }
  
  if (width < MIN_WIDTH || height < MIN_HEIGHT) {
    return { valid: false, error: `Resolution must be at least ${MIN_WIDTH}×${MIN_HEIGHT}` };
  }
  
  if (width > MAX_WIDTH || height > MAX_HEIGHT) {
    return { valid: false, error: `Resolution must not exceed ${MAX_WIDTH}×${MAX_HEIGHT}` };
  }
  
  return { valid: true };
}

/**
 * Estimate compressed file size
 * 
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {Object} profile - Compression profile
 * @returns {number} Estimated size in bytes
 */
export function estimateCompressedSize(originalWidth, originalHeight, profile) {
  // Calculate target dimensions
  const { width, height } = calculateTargetDimensions(
    originalWidth,
    originalHeight,
    profile.maxWidth,
    profile.maxHeight
  );
  
  // Rough estimation based on resolution and quality
  // JPEG compression ratio varies, but this gives a ballpark
  const pixels = width * height;
  const bytesPerPixel = profile.quality * 0.8; // JPEG is roughly 0.5-1.0 bytes per pixel
  
  return Math.round(pixels * bytesPerPixel);
}

// Export for testing
export const _test = {
  calculateTargetDimensions,
};

